// frontend/src/stores/auth.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

// установленное приложение (PWA): вход на 30 дней вместо 7
const inStandalone = () =>
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true

// Определяем baseURL динамически
const getBaseURL = () => {
  if (import.meta.env.PROD) {
    return '/api'
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
}

// Данные из JWT (без проверки подписи — только для выбора интерфейса; доступ защищает сервер)
function decodeToken(t) {
  try {
    const p = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(decodeURIComponent(escape(atob(p))))
  } catch { return null }
}

export const api = axios.create({
  baseURL: getBaseURL()
})

let interceptorsSetup = false

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('token') || null)
  const user = ref(null)
  const isLoading = ref(false)

  const isLoggedIn = computed(() => !!token.value)
  const companyId = computed(() => user.value?.company_id || null)
  const userName = computed(() => user.value?.name || '')
  const userRole = computed(() => user.value?.role || (token.value ? decodeToken(token.value)?.role : null) || null)
  const isMaster = computed(() => userRole.value === 'master')
  const userId = computed(() => user.value?.id || (token.value ? decodeToken(token.value)?.userId : null) || null)

  if (!interceptorsSetup) {
    api.interceptors.request.use((config) => {
      const t = token.value || localStorage.getItem('token')
      if (t) {
        config.headers.Authorization = `Bearer ${t}`
      }
      return config
    })

    api.interceptors.response.use(
      (response) => response,
      (error) => {
        // === 401 — токен истёк или невалиден → разлогин ===
        if (error.response?.status === 401) {
          logout()
          if (!window.location.pathname.includes('/widget')) {
            window.location.href = '/login'
          }
        }
        // === 403 «Токен невалидный» — токен истёк (middleware отвечает 403, не 401) → разлогин ===
        if (error.response?.status === 403 && error.response?.data?.error === 'Токен невалидный') {
          logout()
          if (!window.location.pathname.includes('/widget')) {
            window.location.href = '/login'
          }
        }
        // === 403 COMPANY_DELETED — разлогин с сообщением ===
        if (error.response?.status === 403 && error.response?.data?.code === 'COMPANY_DELETED') {
          logout()
          localStorage.setItem('company_deleted_message', error.response.data.error)
          if (!window.location.pathname.includes('/widget')) {
            window.location.href = '/login?error=company_deleted'
          }
        }
        return Promise.reject(error)
      }
    )
    interceptorsSetup = true
  }

  async function login(email, password) {
    isLoading.value = true
    try {
      const { data } = await api.post('/auth/login', { email, password, pwa: inStandalone() })
      setToken(data.token)
      user.value = data.user
      return data
    } finally {
      isLoading.value = false
    }
  }

  async function register(payload) {
    isLoading.value = true
    try {
      const { data } = await api.post('/auth/register', payload)
      setToken(data.token)
      user.value = data.user
      return data
    } finally {
      isLoading.value = false
    }
  }

  async function fetchUser() {
    if (!token.value) return
    try {
      const { data } = await api.get('/auth/me')
      user.value = data
    } catch {
      logout()
    }
  }

  // Скользящее продление: каждое открытие приложения сдвигает срок входа ещё на 30 дней
  async function refreshSession() {
    if (!token.value || !inStandalone()) return
    try {
      const { data } = await api.post('/auth/refresh', { pwa: true })
      if (data?.token) setToken(data.token)
    } catch { /* при 401 interceptor сам разлогинит */ }
  }

  async function forgotPassword(email) {
    isLoading.value = true
    try {
      const { data } = await api.post('/auth/forgot-password', { email })
      return data
    } finally {
      isLoading.value = false
    }
  }

  async function resetPassword(resetToken, password) {
    isLoading.value = true
    try {
      const { data } = await api.post('/auth/reset-password', { token: resetToken, password })
      return data
    } finally {
      isLoading.value = false
    }
  }

  function setToken(newToken) {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
    } else {
      localStorage.removeItem('token')
    }
  }

function logout() {
  token.value = null
  user.value = null
  localStorage.removeItem('token')
  delete api.defaults.headers.common['Authorization']
  
  // Сброс всех остальных Pinia-сторов
  // Получаем доступ к Pinia через текущий стор
  const pinia = useAuthStore().$pinia
  if (pinia && pinia._s) {
    pinia._s.forEach((store) => {
      if (store.$id !== 'auth' && typeof store.$reset === 'function') {
        store.$reset()
      }
    })
  }
}

  return {
    token, user, isLoading, isLoggedIn, companyId, userName, userRole, isMaster, userId,
    login, register, fetchUser, refreshSession, forgotPassword, resetPassword, setToken, logout
  }
})